import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    ClipboardEdit, Package, Building2, ChevronDown, Loader2,
    Search, Plus, Trash2, AlertTriangle, X, RefreshCw, Send, IndianRupee,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchCCList,
    fetchAmendIndentList,
    fetchIndentCategories,
    checkAmendIndentQty,
    fetchAmendIndentItems,
    fetchItemSuggestions,
    addIndentItem,
    removeIndentItem,
    submitIndentAmend,
    closeIndent,
    clearIndentList,
    clearAmendItems,
    clearItemSuggestions,
    clearQtyCheckResult,
    clearSaveResult,
    clearCloseResult,
    resetIndentAmend,
    selectAmendCCList,
    selectAmendIndentList,
    selectAmendCategoryList,
    selectQtyCheckResult,
    selectAmendItems,
    selectItemSuggestions,
    selectAmendSaveResult,
    selectAmendLoading,
    selectAmendErrors,
} from '../../slices/purchaseSlice/indentAmendSlice';


// Date picker values are JS Dates; sent raw they serialise as UTC ("…T18:30:00Z" = previous day in IST).
// Send the local calendar date instead; strings (already formatted) pass through unchanged.
const toApiDate = (v) => {
    if (!v) return '';
    if (v instanceof Date) {
        return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
    }
    return v;
};

// ── Shared UI primitives (mirrors the SupplierPOAmend / SupplierPOCreation pages) ──

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const SELECTION_TYPES = [
    { value: '1', label: 'Search By Item Code' },
    { value: '2', label: 'Search By Item Name' },
];

// The item was already locked in with a different Basic Price than the item master
// currently has (i.e. the item code was re-priced after this indent was raised).
const priceChanged = (item) => (parseFloat(item.basicprice) || 0) !== (parseFloat(item.IndentBasicPrice) || 0);

// A pending (not-yet-approved) ItemCode Updation blocks amendment on that row entirely.
const isRowLocked = (item) => !!item.ItemEditStatus && item.ItemEditStatus !== 'Approved';

const IndentAmend = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;
    const uid = userData?.UID || userData?.uid || '';

    const ccList       = useSelector(selectAmendCCList);
    const indentList    = useSelector(selectAmendIndentList);
    const categoryList  = useSelector(selectAmendCategoryList);
    const qtyCheckResult= useSelector(selectQtyCheckResult);
    const items          = useSelector(selectAmendItems);
    const suggestions    = useSelector(selectItemSuggestions);
    const saveResult     = useSelector(selectAmendSaveResult);
    const loading         = useSelector(selectAmendLoading);
    const errors           = useSelector(selectAmendErrors);

    const [selectedCC, setSelectedCC] = useState('');
    const [selectedIndentNo, setSelectedIndentNo] = useState('');

    const [category, setCategory] = useState('');
    const [selectionType, setSelectionType] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchBoxRef = useRef(null);

    const [rowEdits, setRowEdits] = useState({});
    const [amendDate, setAmendDate] = useState(todayFormatted());
    const [remarks, setRemarks] = useState('');

    const [showCloseConfirm, setShowCloseConfirm] = useState(false); // pre-load "indent qty is zero" prompt
    const [showCloseOnSubmit, setShowCloseOnSubmit] = useState(false); // submit-time "everything is zero" prompt

    useEffect(() => {
        if (uid && roleId) dispatch(fetchCCList({ uid, rid: roleId }));
        return () => dispatch(resetIndentAmend());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    useEffect(() => {
        if (!category || !selectionType || !searchText.trim()) {
            dispatch(clearItemSuggestions());
            return;
        }
        const t = setTimeout(() => {
            dispatch(fetchItemSuggestions({ prefix: searchText.trim(), type: selectionType, category }));
            setShowSuggestions(true);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, selectionType, category]);

    // Sync row edits whenever the item list changes (initial load or an item add/remove)
    useEffect(() => {
        setRowEdits((prev) => {
            const next = {};
            items.forEach((item) => {
                next[item.IndentListId] = prev[item.IndentListId] || { checked: false, amendType: '', amendQty: '' };
            });
            return next;
        });
    }, [items]);

    useEffect(() => {
        if (qtyCheckResult === 'NotExist') {
            setShowCloseConfirm(true);
        } else if (qtyCheckResult) {
            loadGrid();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qtyCheckResult]);

    useEffect(() => {
        if (saveResult === 'Submited') {
            toast.success('Indent amendment submitted successfully.');
            dispatch(clearSaveResult());
            handleReset();
        } else if (errors.save) {
            toast.error(errors.save);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, errors.save]);

    // ── CC / Indent selection ───────────────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setSelectedIndentNo('');
        setCategory(''); setSelectionType(''); setSearchText('');
        dispatch(clearIndentList());
        dispatch(clearAmendItems());
        dispatch(clearItemSuggestions());
        if (cc) {
            dispatch(fetchAmendIndentList(cc));
            dispatch(fetchIndentCategories(cc));
        }
    };

    const loadGrid = () => {
        dispatch(fetchAmendIndentItems({ ccCode: selectedCC, indentNo: selectedIndentNo }));
        dispatch(clearQtyCheckResult());
    };

    const handleIndentChange = (indentNo) => {
        setSelectedIndentNo(indentNo);
        dispatch(clearAmendItems());
        if (indentNo && selectedCC) {
            dispatch(checkAmendIndentQty({ indentNo, ccCode: selectedCC }));
        }
    };

    const handleCloseNow = async () => {
        setShowCloseConfirm(false);
        const result = await dispatch(closeIndent({ indentNo: selectedIndentNo, createdBy: currentUser, roleId }));
        if (closeIndent.fulfilled.match(result)) {
            toast.success(`Indent ${selectedIndentNo} closed successfully.`);
            handleReset();
        } else {
            toast.error(result.payload || 'Failed to close indent');
        }
        dispatch(clearQtyCheckResult());
    };

    const handleDeclineClose = () => {
        setShowCloseConfirm(false);
        loadGrid();
    };

    // ── Item search / add / remove ──────────────────────────────────────────

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setSelectionType('');
        setSearchText('');
        dispatch(clearItemSuggestions());
    };

    const handleSelectionTypeChange = (e) => {
        if (!category) { toast.error('Select Search By Category'); return; }
        setSelectionType(e.target.value);
        setSearchText('');
        dispatch(clearItemSuggestions());
    };

    const handlePickSuggestion = (s) => {
        setSearchText(s.SearchItem || '');
        setShowSuggestions(false);
    };

    const handleAddItem = async () => {
        if (!category)      { toast.error('Select Search By Category'); return; }
        if (!selectionType) { toast.error('Select Search By Type'); return; }
        if (!searchText.trim()) { toast.error('Enter Search Data'); return; }

        const itemCode = searchText.split(',')[0].trim();
        if (items.some((i) => (i.itemcode || '').trim() === itemCode)) {
            toast.error('Item Code Already Exist');
            return;
        }

        const result = await dispatch(addIndentItem({ itemCode, costCenter: selectedCC, indentNo: selectedIndentNo }));
        if (addIndentItem.fulfilled.match(result)) {
            setSearchText('');
            setShowSuggestions(false);
        } else {
            toast.error(result.payload || 'Failed to add item');
        }
    };

    const handleClearSearch = () => {
        setSearchText('');
        dispatch(clearItemSuggestions());
    };

    const handleDeleteRow = (item) => {
        dispatch(removeIndentItem({ itemcode: item.itemcode, indentListId: item.IndentListId, indentNo: selectedIndentNo }));
    };

    // ── Row edit handlers ────────────────────────────────────────────────────

    const toggleRowChecked = (item) => {
        setRowEdits((prev) => {
            const current = prev[item.IndentListId] || {};
            const nextChecked = !current.checked;

            if (nextChecked && isRowLocked(item)) {
                toast.error(`Item Code ${item.itemcode} Basic Price has been updated from ₹${item.IndentBasicPrice} to ₹${item.basicprice} and is pending verification`);
                return prev;
            }
            return {
                ...prev,
                [item.IndentListId]: nextChecked
                    ? { ...current, checked: true }
                    : { checked: false, amendType: '', amendQty: '' },
            };
        });
    };

    const handleAmendTypeChange = (item, value) => {
        if (value === 'Add' && priceChanged(item) && (parseFloat(item.quantity) || 0) > 0 && (parseFloat(item.IndentBasicPrice) || 0) > 0) {
            toast.error(`Item Code ${item.itemcode} Basic Price changed from ₹${item.IndentBasicPrice} to ₹${item.basicprice} — please Substract the existing quantity instead`);
            return;
        }
        setRowEdits((prev) => ({
            ...prev,
            [item.IndentListId]: { ...prev[item.IndentListId], amendType: value, amendQty: '', checked: true },
        }));
    };

    const handleQtyChange = (item, value) => {
        const e = rowEdits[item.IndentListId] || {};
        const numVal = parseFloat(value);
        if (e.amendType === 'Substract') {
            const cap = parseFloat(item.quantity) || 0;
            if (!isNaN(numVal) && numVal > cap) {
                toast.error(`Substract Quantity cannot exceed the current Indent Quantity (${cap})`);
                value = String(cap);
            }
        }
        setRowEdits((prev) => ({ ...prev, [item.IndentListId]: { ...prev[item.IndentListId], amendQty: value } }));
    };

    // ── Computed totals (mirrors legacy CountSupplierPOAmount()) ────────────

    const rowCalc = useMemo(() => {
        const map = {};
        let plusAmount = 0, minusAmount = 0;
        items.forEach((item) => {
            const e = rowEdits[item.IndentListId];
            const oldQty = parseFloat(item.quantity) || 0;
            const qty = e ? parseFloat(e.amendQty) || 0 : 0;
            let amount = 0, newQty = oldQty;

            if (e?.checked && e.amendType && e.amendQty !== '' && !isNaN(qty)) {
                const useIndentPrice = priceChanged(item) && e.amendType === 'Substract' && (parseFloat(item.IndentBasicPrice) || 0) > 0;
                const price = useIndentPrice ? (parseFloat(item.IndentBasicPrice) || 0) : (parseFloat(item.basicprice) || 0);
                amount = qty * price;
                if (e.amendType === 'Add') {
                    newQty = oldQty + qty;
                    plusAmount += amount;
                } else if (e.amendType === 'Substract') {
                    newQty = oldQty - qty;
                    minusAmount += amount;
                }
            }
            map[item.IndentListId] = { amount, newQty };
        });
        const diffAmount = Math.abs(plusAmount - minusAmount);
        const newQtyTotal = items.reduce((sum, item) => sum + (map[item.IndentListId]?.newQty || 0), 0);
        return { map, plusAmount, minusAmount, diffAmount, newQtyTotal };
    }, [items, rowEdits]);

    const checkedItems = useMemo(() => items.filter((i) => rowEdits[i.IndentListId]?.checked), [items, rowEdits]);

    // ── Submit ───────────────────────────────────────────────────────────────

    const validate = () => {
        if (!checkedItems.length) { toast.error('Verify (check) at least one item to amend'); return false; }
        if (!amendDate) { toast.error('Select Date'); return false; }
        if (!remarks.trim()) { toast.error('Enter Remarks'); return false; }

        for (const item of checkedItems) {
            const e = rowEdits[item.IndentListId];
            if (!e.amendType) { toast.error(`Select Amend Type for ${item.itemcode}`); return false; }
            const qty = parseFloat(e.amendQty) || 0;
            if (qty <= 0) {
                const label = item.ItemStatus === 'New' ? 'Newly added items quantity should not be zero' : 'Enter a valid Amend Quantity';
                toast.error(`${label} (${item.itemcode})`);
                return false;
            }
            if (e.amendType === 'Substract' && qty > (parseFloat(item.quantity) || 0)) {
                toast.error(`Substract Quantity is greater than current Quantity for ${item.itemcode}`);
                return false;
            }
        }
        return true;
    };

    const buildPayload = (closeStatus) => {
        let ids = '', codes = '', basicPrices = '', oldQtys = '', amendTypes = '', amendQtys = '',
            newAmounts = '', oldAmounts = '', newQtys = '', itemTypes = '', itemEditStatuses = '', indentBasicPrices = '';

        checkedItems.forEach((item) => {
            const e = rowEdits[item.IndentListId];
            const calc = rowCalc.map[item.IndentListId] || { amount: 0, newQty: item.quantity };

            ids               += `${item.IndentListId},`;
            codes             += `${item.itemcode},`;
            basicPrices       += `${item.basicprice},`;
            oldQtys           += `${item.quantity},`;
            amendTypes        += `${e.amendType},`;
            amendQtys         += `${parseFloat(e.amendQty) || 0},`;
            newAmounts        += `${calc.amount.toFixed(2)},`;
            oldAmounts        += `${item.Amount},`;
            newQtys           += `${calc.newQty},`;
            itemTypes         += `${item.ItemStatus || 'Old'},`;
            itemEditStatuses  += `${item.ItemEditStatus || ''},`;
            indentBasicPrices += `${item.IndentBasicPrice},`;
        });

        return {
            IndentNo: selectedIndentNo,
            CCCode: selectedCC,
            Indentlistids: ids,
            Itemcodes: codes,
            BasicPrices: basicPrices,
            OldQtys: oldQtys,
            AmendTypes: amendTypes,
            AmendQtys: amendQtys,
            NewAmounts: newAmounts,
            OldAmounts: oldAmounts,
            NewQtys: newQtys,
            Itemtypes: itemTypes,
            AmendDate: toApiDate(amendDate),
            AmendRemarks: remarks,
            CloseStatus: closeStatus,
            ItemEditStatus: itemEditStatuses,
            IndentBasicPrices: indentBasicPrices,
            RoleId: parseInt(roleId, 10) || 0,
            Createdby: currentUser,
        };
    };

    const handleSubmit = () => {
        if (!validate()) return;

        if (rowCalc.newQtyTotal > 0) {
            dispatch(submitIndentAmend(buildPayload('No')));
            return;
        }

        // Every item's effective quantity would become zero — always confirm before proceeding,
        // since declining still needs a coherent amendment to save (see handleDeclineCloseOnSubmit).
        setShowCloseOnSubmit(true);
    };

    const handleConfirmCloseOnSubmit = async () => {
        setShowCloseOnSubmit(false);
        const result = await dispatch(closeIndent({ indentNo: selectedIndentNo, createdBy: currentUser, roleId }));
        if (closeIndent.fulfilled.match(result)) {
            dispatch(submitIndentAmend(buildPayload('Yes')));
        } else {
            toast.error(result.payload || 'Failed to close indent');
        }
    };

    const handleDeclineCloseOnSubmit = () => {
        setShowCloseOnSubmit(false);
        // Declining to close still requires every verified row to carry a real amend
        // quantity — otherwise there is nothing coherent to save.
        const allCheckedHaveQty = checkedItems.every((item) => (parseFloat(rowEdits[item.IndentListId]?.amendQty) || 0) > 0);
        if (allCheckedHaveQty) {
            dispatch(submitIndentAmend(buildPayload('No')));
        } else {
            toast.error('Enter Amend Quantity');
        }
    };

    const handleReset = () => {
        setSelectedCC('');
        setSelectedIndentNo('');
        setCategory(''); setSelectionType(''); setSearchText('');
        setRowEdits({});
        setAmendDate(todayFormatted());
        setRemarks('');
        dispatch(clearIndentList());
        dispatch(clearAmendItems());
        dispatch(clearItemSuggestions());
        dispatch(clearQtyCheckResult());
        dispatch(clearSaveResult());
        dispatch(clearCloseResult());
        if (uid && roleId) dispatch(fetchCCList({ uid, rid: roleId }));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = items.length > 0;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <ClipboardEdit className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Indent Amend</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Add, increase, or reduce item quantities on an open indent.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── CC / Indent selection ─────────────────────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center & Indent" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>Cost Center</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)}>
                                <option value="">-- Please Select --</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 min-w-[240px]">
                        <Label required>Indent No</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedIndentNo} disabled={!selectedCC}
                                onChange={(e) => handleIndentChange(e.target.value)}>
                                <option value="">-- Please Select --</option>
                                {indentList.map((i, idx) => (
                                    <option key={idx} value={i.Indentno}>{i.Indentno}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {(loading.indentList || loading.qtyCheck) ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>

                    {loading.items && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Loading items…</div>
                    )}
                </div>
            </SectionCard>

            {/* ── Item search / add ────────────────────────────────────────── */}
            {selectedIndentNo && gridHasData && (
                <SectionCard icon={Search} title="Add Item to Indent" accent="amber">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="min-w-[200px]">
                            <Label required>Item Category</Label>
                            <select className={inputCls} value={category} onChange={handleCategoryChange}>
                                <option value="">-Please Select-</option>
                                {categoryList.map((c) => (
                                    <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>
                                ))}
                            </select>
                        </div>
                        <div className="min-w-[200px]">
                            <Label required>Search By</Label>
                            <select className={inputCls} value={selectionType} onChange={handleSelectionTypeChange} disabled={!category}>
                                <option value="">-Please Select-</option>
                                {SELECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="relative flex-1 min-w-[260px]" ref={searchBoxRef}>
                            <Label required>Search Item</Label>
                            <input type="text" disabled={!selectionType} placeholder="Start typing to search…"
                                className={`${inputCls} uppercase`}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value.toUpperCase())}
                                onFocus={() => searchText && setShowSuggestions(true)} />
                            {loading.suggestions && (
                                <Loader2 className="absolute right-3 top-9 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                            )}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                                    {suggestions.map((s, i) => (
                                        <button key={i} type="button" onClick={() => handlePickSuggestion(s)}
                                            className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-gray-50 dark:border-gray-700 last:border-0">
                                            {s.SearchItem}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={handleAddItem} disabled={loading.addItem}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.addItem ? <SpinnerIcon /> : <Plus className="w-4 h-4" />} Add Item
                            </button>
                            <button onClick={handleClearSearch}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── Items Grid ───────────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Package} title="Indent Items"
                    subtitle={`${checkedItems.length} of ${items.length} item${items.length !== 1 ? 's' : ''} verified`}
                    accent="purple">
                    <div className="overflow-x-auto rounded-lg border border-purple-200 dark:border-purple-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                                    {['Chk', 'Item', 'DCA / SubDCA', 'Unit', 'Item Price', 'Indent Price', 'Current Qty',
                                      'Amend Type*', 'Amend Qty*', 'Amount', 'New Qty', ''].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => {
                                    const e = rowEdits[item.IndentListId] || {};
                                    const locked = isRowLocked(item);
                                    const changed = priceChanged(item);
                                    const canAdd = (parseFloat(item.quantity) || 0) >= 0; // Add is always allowed per legacy
                                    const canSubstract = (parseFloat(item.quantity) || 0) > 0;
                                    const calc = rowCalc.map[item.IndentListId] || { amount: 0, newQty: item.quantity };
                                    const row = idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-purple-50/40 dark:bg-purple-900/10';
                                    const isNew = item.ItemStatus === 'New';

                                    return (
                                        <tr key={item.IndentListId} className={`${row} border-b border-purple-100 dark:border-purple-800 last:border-0`}>
                                            <td className="px-2.5 py-2">
                                                <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                    checked={!!e.checked} disabled={locked}
                                                    onChange={() => toggleRowChecked(item)} />
                                            </td>
                                            <td className="px-2.5 py-2 max-w-[200px]">
                                                <p className="font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">{item.itemcode?.trim()}</p>
                                                <p className="font-medium text-gray-900 dark:text-white truncate">{item.itemname}</p>
                                                {item.specification && <p className="text-gray-500 dark:text-gray-400 text-[10px] truncate">{item.specification}</p>}
                                                {isNew && <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">New</span>}
                                                {locked && <span className="inline-block mt-0.5 ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">Pending Verification</span>}
                                            </td>
                                            <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{item.dcacode} / {item.subdcacode}</td>
                                            <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{item.units}</td>
                                            <td className={`px-2.5 py-2 text-right whitespace-nowrap ${changed ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                                                ₹{formatIndianCurrency(item.basicprice || 0)}
                                            </td>
                                            <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">₹{formatIndianCurrency(item.IndentBasicPrice || 0)}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{item.quantity}</td>

                                            <td className="px-1.5 py-1.5">
                                                <select className={`${inputCls} w-28`} disabled={locked}
                                                    value={e.amendType || ''}
                                                    onChange={(ev) => handleAmendTypeChange(item, ev.target.value)}>
                                                    <option value="">Select</option>
                                                    {canAdd && <option value="Add">Add</option>}
                                                    {canSubstract && <option value="Substract">Substract</option>}
                                                </select>
                                            </td>
                                            <td className="px-1.5 py-1.5">
                                                <input type="number" min="0" step="0.0001" disabled={locked || !e.amendType}
                                                    className={`${inputCls} w-20 text-right`}
                                                    value={e.amendQty ?? ''}
                                                    onChange={(ev) => handleQtyChange(item, ev.target.value)} />
                                            </td>
                                            <td className={`px-2.5 py-2 text-right font-semibold whitespace-nowrap ${e.amendType === 'Substract' ? 'text-red-600 dark:text-red-400' : e.amendType === 'Add' ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
                                                ₹{formatIndianCurrency(calc.amount || 0)}
                                            </td>
                                            <td className="px-2.5 py-2 text-right font-medium text-indigo-700 dark:text-indigo-400">{calc.newQty}</td>
                                            <td className="px-1.5 py-1.5">
                                                <button onClick={() => handleDeleteRow(item)} disabled={loading.removeItem}
                                                    className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 disabled:opacity-50">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </SectionCard>
            )}

            {/* ── Value summary, Date, Remarks, Submit ────────────────────── */}
            {gridHasData && (
                <SectionCard icon={IndianRupee} title="Amendment Summary" accent="amber">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Added Value (+)</p>
                            <p className="text-sm font-bold text-green-600 dark:text-green-400">₹{formatIndianCurrency(rowCalc.plusAmount)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reduced Value (-)</p>
                            <p className="text-sm font-bold text-red-600 dark:text-red-400">₹{formatIndianCurrency(rowCalc.minusAmount)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Difference</p>
                            <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400">₹{formatIndianCurrency(rowCalc.diffAmount)}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label required>Amend Date</Label>
                            <CustomDatePicker value={amendDate} onChange={setAmendDate}
                                format="DD-MMM-YYYY" placeholder="Select Amend Date" maxDate={todayFormatted()} />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter reason for amendment…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button onClick={handleSubmit} disabled={loading.save || loading.close}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {(loading.save || loading.close) ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.save ? 'Saving…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}

            {/* ── "Indent qty is zero" prompt (shown right after selecting the indent) ── */}
            {showCloseConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                            <h3 className="font-bold text-gray-900 dark:text-white">Indent Quantity is Zero</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                            Indent Item Quantity is Zero. Do you want to close Indent No: <span className="font-semibold">{selectedIndentNo}</span>?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button onClick={handleDeclineClose}
                                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                No
                            </button>
                            <button onClick={handleCloseNow} disabled={loading.close}
                                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.close ? 'Closing…' : 'Yes, Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── "everything would become zero on submit" prompt ─────────── */}
            {showCloseOnSubmit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                            <h3 className="font-bold text-gray-900 dark:text-white">Close Indent?</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                            This amendment reduces every item to zero quantity. Do you want to close Indent No: <span className="font-semibold">{selectedIndentNo}</span>?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button onClick={handleDeclineCloseOnSubmit}
                                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                No
                            </button>
                            <button onClick={handleConfirmCloseOnSubmit} disabled={loading.close}
                                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.close ? 'Closing…' : 'Yes, Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IndentAmend;
